/**
 * Flow to Scene Converter
 * Converts Flow nodes and edges into ISceneObj format.
 *
 * This file provides two conversion interfaces:
 * - `graphToScene()` - simpler interface used by FlowEditor/FlowCanvas
 * - `flowToScene()` - used by reactive stores with metadata
 *
 * Uses Flow-specific helpers from ./helpers/ for scene JSON output (independent of Scenes).
 */

import type { FlowNode, FlowEdge } from '../types/flow-types';
import type { ISceneObj } from '../../../types/SceneType';
import {
	createDeviceTriggerFromFlow,
	createDeviceConditionFromFlow,
	buildDeviceConditionRef,
	buildBareReactiveTrigger,
} from './helpers';
import { nodeToSceneJson } from '../nodes/definitions/registry';
import { get } from 'svelte/store';
import { devices, devices_status, devices_settings } from '../../../stores';
import { get_mode, real_id } from '../../../helpers/interface_helpers';
import {
	buildIslandTriggerGate,
	buildReactiveTopIfFromDeviceRefs,
	collectDeviceRefsFromIfExpr,
	extractDeviceRefFromConditionJson,
} from './flow-island-utils';

export interface FlowMetadata {
	name: string;
	room: number | string;
	roomName: string;
	activeTimeIntervals?: any[];
	image?: string;
	backgroundColor?: string;
	integrations?: boolean;
}

// ---- Adjacency map helpers ----

interface EdgeMaps {
	/** outgoing edges per source node id */
	adjOut: Map<string, FlowEdge[]>;
	/** incoming edges per target node id */
	adjIn: Map<string, FlowEdge[]>;
}

function buildEdgeMaps(edges: FlowEdge[]): EdgeMaps {
	const adjOut = new Map<string, FlowEdge[]>();
	const adjIn = new Map<string, FlowEdge[]>();
	for (const edge of edges) {
		if (!adjOut.has(edge.source)) adjOut.set(edge.source, []);
		adjOut.get(edge.source)!.push(edge);
		if (!adjIn.has(edge.target)) adjIn.set(edge.target, []);
		adjIn.get(edge.target)!.push(edge);
	}
	return { adjOut, adjIn };
}

/** Sort edges by logic-gate input port (`input-0`, `input-1`, …) for stable serialization. */
function gateInputIndex(targetHandle: string | null | undefined): number {
	if (typeof targetHandle === 'string' && targetHandle.startsWith('input-')) {
		const n = parseInt(targetHandle.slice('input-'.length), 10);
		return Number.isFinite(n) ? n : 999;
	}
	return 999;
}

function sortGateInputEdges(edges: FlowEdge[]): FlowEdge[] {
	return edges.slice().sort((a, b) => gateInputIndex(a.targetHandle) - gateInputIndex(b.targetHandle));
}

function isFalseBranchSourceHandle(handle: string | null | undefined): boolean {
	if (handle === 'false') return true;
	return typeof handle === 'string' && handle.includes('false');
}

/**
 * Unary negation for scene `if` subtrees. Matches JEMMS eval `not` over nested condition objects.
 */
function negateConditionBlock(expr: any): any {
	if (!expr || typeof expr !== 'object') return expr;
	return { not: expr, _gui_purpose: 'condition' };
}

/**
 * Convert a flow graph structure back into a Shelly scene object.
 * This is the simpler interface used by FlowEditor and FlowCanvas
 * that works directly with an existing scene object.
 */
export function graphToScene(
	nodes: FlowNode[],
	edges: FlowEdge[],
	existingScene?: ISceneObj,
): ISceneObj {
	const scene: ISceneObj = existingScene
		? JSON.parse(JSON.stringify(existingScene))
		: {
				_enabled: false,
				_run_on_ingest: false,
				_meta: {
					name: 'Untitled Flow',
					image: 'fa-diagram-project',
					backgroundColor: 'rgb(102, 96, 237)',
					integrations: false,
					room: -1,
					roomn: '',
					position: -1,
					flowEdited: true,
					isFlow: true,
					sceneType: 'flow',
				},
				if: { or: [] },
				do: [],
				else: [],
			};

	if (scene._meta) {
		scene._meta.flowEdited = true;
		scene._meta.isFlow = true;
		scene._meta.sceneType = 'flow';
	}

	const result = buildSceneFromGraph(nodes, edges);

	scene.if = result.ifBlock;
	scene.do = result.doActions;
	scene.else = result.elseActions;

	extractDeviceMetadata(scene, nodes);
	persistFlowGraphMeta(scene, nodes, edges);

	return scene;
}

/**
 * Convert Flow nodes and edges to ISceneObj with full metadata.
 * Used by the reactive store for real-time conversion.
 */
export function flowToScene(nodes: FlowNode[], edges: FlowEdge[], metadata: FlowMetadata): ISceneObj {
	const scene: ISceneObj = {
		_enabled: false,
		_run_on_ingest: false,
		_meta: {
			name: metadata.name || 'Untitled Flow',
			image: metadata.image || 'default',
			backgroundColor: metadata.backgroundColor || '#4A90E2',
			integrations: metadata.integrations !== false,
			room: metadata.room || 0,
			roomn: metadata.roomName || '',
			flowEdited: true,
			isFlow: true,
			sceneType: 'flow',
		},
		if: { or: [] },
		do: [],
		else: [],
	};

	const result = buildSceneFromGraph(nodes, edges);

	scene.if = result.ifBlock;
	scene.do = result.doActions;
	scene.else = result.elseActions;

	if (metadata.activeTimeIntervals && metadata.activeTimeIntervals.length > 0) {
		const existingOr = scene.if.or || [];
		scene.if = {
			and: [{ or: existingOr }, { indailyint: metadata.activeTimeIntervals }],
		} as any;
	}

	extractDeviceMetadata(scene, nodes);
	persistFlowGraphMeta(scene, nodes, edges);

	return scene;
}

// ---- Core graph-to-scene conversion logic ----

interface SceneFromGraph {
	ifBlock: ISceneObj['if'];
	doActions: any[];
	elseActions: any[];
}

function buildSceneFromGraph(nodes: FlowNode[], edges: FlowEdge[]): SceneFromGraph {
	// Editor-only nodes (e.g. sticky notes) must not affect scene if/do/else.
	const semanticNodes = nodes.filter((n) => n.type !== 'note');
	const semanticIds = new Set(semanticNodes.map((n) => n.id));
	const semanticEdges = edges.filter(
		(e) => semanticIds.has(String(e.source)) && semanticIds.has(String(e.target)),
	);

	const nodeMap = new Map(semanticNodes.map((n) => [n.id, n]));
	// Build adjacency maps once — all traversal functions use O(1) lookups.
	const em = buildEdgeMaps(semanticEdges);

	const triggerNodes = semanticNodes.filter((n) => n.type === 'trigger');
	const conditionLikeNodes = semanticNodes.filter((n) =>
		[
			'condition',
			'timeCondition',
			'manualCondition',
			'weatherCondition',
			'sunriseSunsetCondition',
			'energyCondition',
			'mathCondition',
			'sceneCondition',
			'alarmCondition',
			'variableCondition',
			'virtualComponentEvent',
			'virtualComponentCondition',
		].includes(n.type || ''),
	);

	const logicGateNodes = semanticNodes.filter((n) => n.type === 'logicGate');
	const orGateNodes = logicGateNodes.filter((n) => (n.data.gateType as string) === 'or');
	const andGateNodes = logicGateNodes.filter((n) => (n.data.gateType as string) === 'and');
	const norGateNodes = logicGateNodes.filter((n) => (n.data.gateType as string) === 'nor');

	const actionNodes = semanticNodes.filter((n) =>
		[
			'action',
			'notificationAction',
			'groupAction',
			'sceneAction',
			'alarmAction',
			'scriptAction',
			'setVariable',
			'actionSequence',
			'virtualComponentAction',
		].includes(n.type || ''),
	);
	const scheduleNodes = semanticNodes.filter((n) => n.type === 'scheduleCondition');

	// ── Root switch node ──────────────────────────────────────────────────────
	// A switch node at the root becomes the reactive trigger.
	// Its `if` block is a bare rr subscription; the case chain goes into `do`.
	const rootSwitch =
		semanticNodes.some((n) => n.type === 'switchNode') ||
		semanticNodes.some((n) => n.type === 'variableSwitch')
			? findRootSwitchNode(semanticNodes, em)
			: null;
	const rootHysteresis = semanticNodes.find(
		(n) => n.type === 'hysteresisCondition' && (em.adjIn.get(n.id) ?? []).length === 0,
	);
	const rootDecisions = findRootDecisionNodes(semanticNodes, em, nodeMap);
	if (rootHysteresis && rootDecisions.length === 1 && rootDecisions[0]?.id === rootHysteresis.id) {
		const ref = getSwitchDeviceRef(rootHysteresis);
		const bareIf = ref
			? { or: [{ neq: [{ rr: ref }, null], _gui_purpose: 'condition' }] }
			: { or: [] };
		const chain = serializeHysteresisNode(
			rootHysteresis,
			em,
			nodeMap,
			triggerNodes,
			new Set([rootHysteresis.id]),
		);
		return {
			ifBlock: mergeIfWithSchedule(bareIf, scheduleNodes),
			doActions: chain ? [chain] : [],
			elseActions: [],
		};
	}
	if (rootHysteresis && !rootSwitch) {
		const otherRootDecisions = rootDecisions.filter((root) => root.id !== rootHysteresis.id);
		if (otherRootDecisions.length > 0) {
			const hysteresisRef = getSwitchDeviceRef(rootHysteresis);
			const hysteresisChain = serializeHysteresisNode(
				rootHysteresis,
				em,
				nodeMap,
				triggerNodes,
				new Set([rootHysteresis.id]),
			);
			const decisionIslands = otherRootDecisions.map((root) => {
				const innerIf = buildIfBlockFromDecision(root.id, em, nodeMap, triggerNodes, {
					nested: true,
				});
				return {
					body: {
						if: innerIf,
						do: blockValue(
							serializeDecisionBranch(root.id, 'true', em, nodeMap, triggerNodes),
						),
						else: blockValue(
							serializeDecisionBranch(root.id, 'false', em, nodeMap, triggerNodes),
						),
					},
					gateRefs: collectDeviceRefsFromIfExpr(innerIf),
					topRefs: collectReactiveRefs(
						buildIfBlockFromDecision(root.id, em, nodeMap, triggerNodes),
					),
				};
			});
			const allFlowGateRefs = [
				...(hysteresisRef.startsWith('dev:') ? [hysteresisRef] : []),
				...decisionIslands.flatMap((entry) => entry.gateRefs),
			];
			const blk: any[] = [
				hysteresisRef.startsWith('dev:')
					? {
							_gui_island_gate: true,
							if: buildIslandTriggerGate([hysteresisRef], allFlowGateRefs),
							do: hysteresisChain,
						}
					: hysteresisChain,
			];
			for (const { body, gateRefs } of decisionIslands) {
				blk.push(
					gateRefs.length > 0
						? {
								_gui_island_gate: true,
								if: buildIslandTriggerGate(gateRefs, allFlowGateRefs),
								do: body,
							}
						: body,
				);
			}
			const topRefs = [hysteresisRef, ...decisionIslands.flatMap((entry) => entry.topRefs)].filter(
				Boolean,
			);
			return {
				ifBlock: mergeIfWithSchedule(
					buildReactiveTopIfFromDeviceRefs([...new Set(topRefs)]),
					scheduleNodes,
				),
				doActions: { blk } as any,
				elseActions: [],
			};
		}
	}
	if (rootSwitch && rootDecisions.length === 0) {
		const ref = getSwitchSourceRef(rootSwitch);
		// Build the bare reactive trigger directly from the already-resolved ref so
		// the correct property (e.g. 'gain' for SHCB-1 in color mode) is used.
		const bareIf = ref
			? { or: [{ neq: [{ rr: ref }, null], _gui_purpose: 'condition' }] }
			: { or: [] };
		const switchChain = serializeSwitchNode(
			rootSwitch,
			em,
			nodeMap,
			triggerNodes,
			new Set([rootSwitch.id]),
		);
		return {
			ifBlock: mergeIfWithSchedule(bareIf, scheduleNodes),
			doActions: switchChain ? [switchChain] : [],
			elseActions: [],
		};
	}

	if (rootSwitch && rootDecisions.length > 0) {
		const switchRef = getSwitchSourceRef(rootSwitch);
		const switchChain = serializeSwitchNode(
			rootSwitch,
			em,
			nodeMap,
			triggerNodes,
			new Set([rootSwitch.id]),
		);
		const decisionIslands = rootDecisions.map((root) => {
			const innerIf = buildIfBlockFromDecision(root.id, em, nodeMap, triggerNodes, {
				nested: true,
			});
			return {
				body: {
					if: innerIf,
					do: blockValue(serializeDecisionBranch(root.id, 'true', em, nodeMap, triggerNodes)),
					else: blockValue(
						serializeDecisionBranch(root.id, 'false', em, nodeMap, triggerNodes),
					),
				},
				gateRefs: collectDeviceRefsFromIfExpr(innerIf),
				topRefs: collectReactiveRefs(
					buildIfBlockFromDecision(root.id, em, nodeMap, triggerNodes),
				),
			};
		});
		const allFlowGateRefs = [
			...(switchRef.startsWith('dev:') ? [switchRef] : []),
			...decisionIslands.flatMap((entry) => entry.gateRefs),
		];
		const blk: any[] = [];
		if (switchChain) {
			blk.push(
				switchRef.startsWith('dev:')
					? {
							_gui_island_gate: true,
							if: buildIslandTriggerGate([switchRef], allFlowGateRefs),
							do: switchChain,
						}
					: switchChain,
			);
		}
		for (const { body, gateRefs } of decisionIslands) {
			blk.push(
				gateRefs.length > 0
					? {
							_gui_island_gate: true,
							if: buildIslandTriggerGate(gateRefs, allFlowGateRefs),
							do: body,
						}
					: body,
			);
		}

		const topRefs = [switchRef, ...decisionIslands.flatMap((entry) => entry.topRefs)].filter(
			Boolean,
		);
		return {
			ifBlock: mergeIfWithSchedule(
				buildReactiveTopIfFromDeviceRefs([...new Set(topRefs)]),
				scheduleNodes,
			),
			doActions: { blk } as any,
			elseActions: [],
		};
	}

	if (rootDecisions.length === 1) {
		const root = rootDecisions[0];
		const ifBlock = buildIfBlockFromDecision(root.id, em, nodeMap, triggerNodes);
		const doActions = serializeDecisionBranch(root.id, 'true', em, nodeMap, triggerNodes);
		const elseActions = serializeDecisionBranch(root.id, 'false', em, nodeMap, triggerNodes);
		return {
			ifBlock: mergeIfWithSchedule(ifBlock, scheduleNodes),
			doActions,
			elseActions,
		};
	}
	if (rootDecisions.length > 1) {
		const islandBodies: {
			body: { if: unknown; do: unknown; else: unknown };
			gateRefs: string[];
		}[] = [];
		for (const root of rootDecisions) {
			const doSt = serializeDecisionBranch(root.id, 'true', em, nodeMap, triggerNodes);
			const elseSt = serializeDecisionBranch(root.id, 'false', em, nodeMap, triggerNodes);
			const innerIslandIf = buildIfBlockFromDecision(root.id, em, nodeMap, triggerNodes, {
				nested: true,
			});
			islandBodies.push({
				body: {
					if: innerIslandIf,
					do: blockValue(doSt),
					else: blockValue(elseSt),
				},
				gateRefs: collectDeviceRefsFromIfExpr(innerIslandIf),
			});
		}
		const allFlowGateRefs = [...new Set(islandBodies.flatMap((entry) => entry.gateRefs))];
		const blk: any[] = islandBodies.map(({ body, gateRefs }) => {
			if (gateRefs.length > 0) {
				return {
					_gui_island_gate: true,
					if: buildIslandTriggerGate(gateRefs, allFlowGateRefs),
					do: body,
				};
			}
			return body;
		});
		const ifBlock = buildAllDevicesReactiveTriggerIf(semanticNodes, em, nodeMap, triggerNodes);
		return {
			ifBlock: mergeIfWithSchedule(ifBlock, scheduleNodes),
			doActions: { blk } as any,
			elseActions: [],
		};
	}

	// Flat fallback when no clear decision root is found.
	const hasNorGate = norGateNodes.length > 0;
	const effectiveOrGates = [...orGateNodes, ...norGateNodes];
	const ifBlock = buildIfBlock(
		triggerNodes,
		conditionLikeNodes,
		effectiveOrGates,
		andGateNodes,
		em,
		nodeMap,
	);
	const { doActions, elseActions } = buildActionChains(actionNodes, em);
	if (hasNorGate) {
		return {
			ifBlock: mergeIfWithSchedule(ifBlock, scheduleNodes),
			doActions: elseActions,
			elseActions: doActions,
		};
	}
	return { ifBlock: mergeIfWithSchedule(ifBlock, scheduleNodes), doActions, elseActions };
}

const ACTION_NODE_TYPES = new Set([
	'action',
	'notificationAction',
	'groupAction',
	'sceneAction',
	'alarmAction',
	'scriptAction',
	'setVariable',
	'actionSequence',
	'virtualComponentAction',
]);

function isActionNodeType(type?: string): boolean {
	return ACTION_NODE_TYPES.has(type || '');
}

function isDecisionNodeType(type?: string): boolean {
	return [
		'trigger',
		'condition',
		'timeCondition',
		'manualCondition',
		'weatherCondition',
		'sunriseSunsetCondition',
		'energyCondition',
		'mathCondition',
		'sceneCondition',
		'alarmCondition',
		'scheduleCondition',
		'variableCondition',
		'hysteresisCondition',
		'presenceCondition',
		'virtualComponentEvent',
		'virtualComponentCondition',
		'logicGate',
	].includes(type || '');
}

/**
 * AND-combine `if` with scene-wide or schedule intervals as `{ indailyint: intervals }`.
 * Exported for FlowEditor save so flow settings “Active Time Windows” persist into `scene.if`.
 */
export function mergeIfWithIndailyintIntervals(
	ifBlock: ISceneObj['if'],
	intervals: any[] | undefined | null,
): ISceneObj['if'] {
	const list = Array.isArray(intervals) ? intervals.filter((x) => x != null) : [];
	if (list.length === 0) return ifBlock;

	const hasAnd = (ifBlock as any)?.and && Array.isArray((ifBlock as any).and);
	if (hasAnd) {
		return {
			and: [...((ifBlock as any).and || []), { indailyint: list }],
		} as any;
	}

	return {
		and: [ifBlock as any, { indailyint: list }],
	} as any;
}

function mergeIfWithSchedule(ifBlock: ISceneObj['if'], scheduleNodes: FlowNode[]): ISceneObj['if'] {
	const intervals = scheduleNodes
		.flatMap((n) =>
			Array.isArray((n.data as any).activeTimeIntervals)
				? ((n.data as any).activeTimeIntervals as any[])
				: [],
		)
		.filter(Boolean);
	return mergeIfWithIndailyintIntervals(ifBlock, intervals);
}

function isBranchSourceHandle(handle: string | null | undefined): boolean {
	return (
		handle === 'true' ||
		handle === 'false' ||
		(typeof handle === 'string' && (handle.includes('true') || handle.includes('false')))
	);
}

function isLogicGateInputHandle(targetHandle: string | null | undefined): boolean {
	return typeof targetHandle === 'string' && targetHandle.startsWith('input-');
}

/** Top-level reactive trigger: OR of distinct device subscriptions (fires on any property change). */
function buildAllDevicesReactiveTriggerIf(
	nodes: FlowNode[],
	em: EdgeMaps,
	nodeMap: Map<string, FlowNode>,
	triggerNodes: FlowNode[],
): ISceneObj['if'] {
	const refs = new Set<string>();
	for (const node of nodes) {
		if (
			node.type !== 'condition' &&
			node.type !== 'presenceCondition' &&
			node.type !== 'virtualComponentEvent' &&
			node.type !== 'virtualComponentCondition' &&
			node.type !== 'trigger'
		)
			continue;
		const json = resolveNodeToSceneJson(node, triggerNodes, em, { nested: true });
		const ref = extractDeviceRefFromConditionJson(json);
		if (ref) refs.add(ref);
	}
	return buildReactiveTopIfFromDeviceRefs(Array.from(refs)) as ISceneObj['if'];
}

/** Collect every reactive resource used by a trigger expression, including dt:* timers. */
function collectReactiveRefs(value: unknown): string[] {
	const refs = new Set<string>();
	const walk = (entry: unknown): void => {
		if (Array.isArray(entry)) {
			entry.forEach(walk);
			return;
		}
		if (!entry || typeof entry !== 'object') return;
		const record = entry as Record<string, unknown>;
		if (typeof record.rr === 'string' && record.rr.trim()) refs.add(record.rr);
		Object.values(record).forEach(walk);
	};
	walk(value);
	return [...refs];
}

/** Logic gates fed by other decision nodes are merge nodes, not independent flow roots. */
function logicGateHasDecisionInputs(
	nodeId: string,
	em: EdgeMaps,
	nodeMap: Map<string, FlowNode>,
): boolean {
	return (em.adjIn.get(nodeId) ?? []).some((edge) => {
		if (!isLogicGateInputHandle(edge.targetHandle)) return false;
		const src = nodeMap.get(edge.source);
		return src != null && isDecisionNodeType(src.type);
	});
}

/** Condition/trigger has a branch edge to a non-gate-input target (standalone action or nested decision). */
function hasStandaloneBranchOutput(node: FlowNode, em: EdgeMaps): boolean {
	return (em.adjOut.get(node.id) ?? []).some(
		(e) => isBranchSourceHandle(e.sourceHandle) && !isLogicGateInputHandle(e.targetHandle),
	);
}

/** All disconnected flow roots (sorted) — each maps 1:1 to `scene.if.or[i]` and `do.blk[i]`. */
function findRootDecisionNodes(
	nodes: FlowNode[],
	em: EdgeMaps,
	nodeMap: Map<string, FlowNode>,
): FlowNode[] {
	const decisionNodes = nodes.filter((n) => isDecisionNodeType(n.type));

	const reachableViaDecisionBranch = new Set<string>();
	for (const node of decisionNodes) {
		for (const edge of em.adjOut.get(node.id) ?? []) {
			if (isBranchSourceHandle(edge.sourceHandle) && !isLogicGateInputHandle(edge.targetHandle)) {
				reachableViaDecisionBranch.add(edge.target);
			}
		}
	}

	const candidates = decisionNodes.filter((node) => {
		if (reachableViaDecisionBranch.has(node.id)) return false;
		const outgoing = em.adjOut.get(node.id) ?? [];
		const hasBranchOutputs = outgoing.some(
			(e) => isBranchSourceHandle(e.sourceHandle) && !isLogicGateInputHandle(e.targetHandle),
		);
		// Merge-only gates (inputs but no true/false → actions) are not roots.
		if (node.type === 'logicGate' && logicGateHasDecisionInputs(node.id, em, nodeMap)) {
			return hasBranchOutputs;
		}
		if (
			node.type === 'condition' ||
			node.type === 'presenceCondition' ||
			node.type === 'virtualComponentEvent' ||
			node.type === 'virtualComponentCondition' ||
			node.type === 'trigger'
		) {
			return hasStandaloneBranchOutput(node, em);
		}
		return hasBranchOutputs;
	});

	if (candidates.length > 0) {
		candidates.sort((a, b) => {
			const outA = (em.adjOut.get(a.id) ?? []).length;
			const outB = (em.adjOut.get(b.id) ?? []).length;
			if (outB !== outA) return outB - outA;
			const inA = (em.adjIn.get(a.id) ?? []).filter(
				(e) => nodeMap.get(e.source)?.type === 'trigger',
			).length;
			const inB = (em.adjIn.get(b.id) ?? []).filter(
				(e) => nodeMap.get(e.source)?.type === 'trigger',
			).length;
			if (inB !== inA) return inB - inA;
			return (a.position?.x ?? 0) - (b.position?.x ?? 0);
		});
		return candidates;
	}

	const fallback = decisionNodes[0];
	return fallback ? [fallback] : [];
}

function buildIfBlockFromDecision(
	decisionNodeId: string,
	em: EdgeMaps,
	nodeMap: Map<string, FlowNode>,
	triggerNodes: FlowNode[],
	context?: { nested?: boolean },
): ISceneObj['if'] {
	const node = nodeMap.get(decisionNodeId);
	if (!node) return { or: [] };

	if (node.type === 'logicGate') {
		const gateType = String(node.data.gateType || 'or');
		const sortedIn = sortGateInputEdges(em.adjIn.get(node.id) ?? []);

		const childExprs = sortedIn
			.map((edge) => {
				const src = nodeMap.get(edge.source);
				if (!src || !isDecisionNodeType(src.type)) return null;
				let inner = buildIfBlockFromDecision(src.id, em, nodeMap, triggerNodes, context);
				if (!inner) return null;
				if (isFalseBranchSourceHandle(edge.sourceHandle)) {
					inner = negateConditionBlock(inner);
				}
				return inner;
			})
			.filter((expr) => !!expr);

		if (gateType === 'and') {
			return {
				and: childExprs.map((expr: any) => {
					if (expr?.or && Array.isArray(expr.or)) return { or: expr.or };
					return { or: [expr] };
				}),
			} as any;
		}

		// OR/NOR share the same condition expression. NOR inversion is handled
		// during branch serialization by swapping true/false handles.
		const orList: any[] = [];
		for (const expr of childExprs as any[]) {
			if (expr?.or && Array.isArray(expr.or)) orList.push(...expr.or);
			else orList.push(expr);
		}
		return { or: orList } as any;
	}

	const resolved = resolveNodeToSceneJson(node, triggerNodes, em, context);
	return { or: resolved ? [resolved] : [] };
}

function serializeDecisionBranch(
	decisionNodeId: string,
	branch: 'true' | 'false',
	em: EdgeMaps,
	nodeMap: Map<string, FlowNode>,
	triggerNodes: FlowNode[],
): any[] {
	const decisionNode = nodeMap.get(decisionNodeId);
	if (!decisionNode) return [];

	let effectiveBranch = branch;
	if (decisionNode.type === 'logicGate' && String(decisionNode.data.gateType || 'or') === 'nor') {
		effectiveBranch = branch === 'true' ? 'false' : 'true';
	}

	const outgoing = (em.adjOut.get(decisionNodeId) ?? []).filter((e) => {
		if (isLogicGateInputHandle(e.targetHandle)) return false;
		if (decisionNode.type === 'trigger' && (e.sourceHandle === 'output' || !e.sourceHandle)) {
			return effectiveBranch === 'true';
		}
		return e.sourceHandle === effectiveBranch || e.sourceHandle?.includes(effectiveBranch);
	});

	const statements: any[] = [];
	// Each parallel branch gets its own visited set so one root’s walk does not block another’s
	// (shared visited was dropping sibling action chains). Deduplicate by _fid for merge Diamond DAGs.
	for (const edge of outgoing) {
		const branchVisited = new Set<string>([decisionNodeId]);
		statements.push(...serializeFromNode(edge.target, em, nodeMap, triggerNodes, branchVisited));
	}
	return dedupeFlatBranchStatementsByFid(statements);
}

/**
 * Remove duplicate leaf statements that share the same _fid (e.g. two edges into one Run Scene node).
 * Preserves nested `{ if, do, else }` entries; only collapses duplicate JSON objects with `_fid`.
 */
function dedupeFlatBranchStatementsByFid(stmts: any[]): any[] {
	const seen = new Set<string>();
	const out: any[] = [];
	for (const s of stmts) {
		if (!s || typeof s !== 'object') {
			out.push(s);
			continue;
		}
		if (s.if !== undefined) {
			out.push(s);
			continue;
		}
		const fid = s._fid;
		if (fid != null && fid !== '') {
			const key = String(fid);
			if (seen.has(key)) continue;
			seen.add(key);
		}
		out.push(s);
	}
	return out;
}

function serializeFromNode(
	nodeId: string,
	em: EdgeMaps,
	nodeMap: Map<string, FlowNode>,
	triggerNodes: FlowNode[],
	visited: Set<string>,
): any[] {
	if (visited.has(nodeId)) return [];
	visited.add(nodeId);

	const node = nodeMap.get(nodeId);
	if (!node) return [];

	if (node.type === 'hysteresisCondition') {
		return [serializeHysteresisNode(node, em, nodeMap, triggerNodes, visited)];
	}

	if (isDecisionNodeType(node.type)) {
		const nestedIf = buildIfBlockFromDecision(node.id, em, nodeMap, triggerNodes, { nested: true });
		const nestedDo = serializeDecisionBranch(node.id, 'true', em, nodeMap, triggerNodes);
		const nestedElse = serializeDecisionBranch(node.id, 'false', em, nodeMap, triggerNodes);
		return [
			{
				if: nestedIf,
				do: blockValue(nestedDo),
				else: blockValue(nestedElse),
			},
		];
	}

	if (node.type === 'timer') {
		const delay = node.data.delay;
		const nextEdges = (em.adjOut.get(node.id) ?? []).filter(
			(e) => !e.sourceHandle || e.sourceHandle === 'output' || e.sourceHandle?.includes('output'),
		);
		const nextStatements: any[] = [];
		for (const nextEdge of nextEdges) {
			nextStatements.push(
				...serializeFromNode(nextEdge.target, em, nodeMap, triggerNodes, new Set(visited)),
			);
		}
		const dedupedNext = dedupeFlatBranchStatementsByFid(nextStatements);
		if (delay?.value > 0 && dedupedNext.length > 0) {
			return [
				{
					wait: (delay as any).value,
					units: (delay as any).units === 'min' ? 'minutes' : 'sec',
					wid: node.data._wid || Date.now(),
					_fid: node.id,
					_flow_card: String(node.data.paletteKey || 'timer'),
					_flow_card_version: '1.0.0',
					do: blockValue(dedupedNext),
				},
			];
		}
		return dedupedNext;
	}

	if (node.type === 'switchNode' || node.type === 'variableSwitch') {
		return [serializeSwitchNode(node, em, nodeMap, triggerNodes, visited)];
	}

	if (node.type === 'actionSequence') {
		const sequence = serializeActionSequence(node);
		const statements: any[] = sequence ? [sequence] : [];
		for (const edge of em.adjOut.get(node.id) ?? []) {
			if (!edge.sourceHandle || edge.sourceHandle === 'output') {
				statements.push(...serializeFromNode(edge.target, em, nodeMap, triggerNodes, visited));
			}
		}
		return statements;
	}

	if (isActionNodeType(node.type)) {
		const statements: any[] = [];
		const action = extractSceneActionData(node);
		if (action) statements.push(action);

		const nextEdges = (em.adjOut.get(node.id) ?? []).filter(
			(e) => !e.sourceHandle || e.sourceHandle === 'output' || e.sourceHandle?.includes('output'),
		);
		for (const nextEdge of nextEdges) {
			statements.push(...serializeFromNode(nextEdge.target, em, nodeMap, triggerNodes, visited));
		}
		return statements;
	}

	return [];
}

type SwitchCase = {
	id: string;
	label: string;
	operator: string;
	value: string;
	value2?: string;
};

/**
 * Build the device ref string for a switch node.
 * Falls back to a legacy `sourceRef` field for backwards-compat.
 *
 * devMode is resolved from the live device state (same as WhenStep.svelte) so
 * devices like SHCB-1 in color mode correctly map 'brightness' → 'gain' instead
 * of 'brightness1'.  node.data.devMode is only used as a fallback when no live
 * device is found (e.g. device is offline/unavailable).
 */
function getSwitchDeviceRef(node: FlowNode): string {
	if (node.data.deviceId) {
		const deviceId = String(node.data.deviceId);
		const sourceRef = String(node.data.sourceRef || '');
		const sourceParts = sourceRef.split(':');
		const sourceDeviceId = sourceParts[0] === 'dev' ? sourceParts[1] || '' : '';
		const sourceProperty = sourceParts.length > 2 ? sourceParts.slice(2).join(':') : '';

		// A scene reconstructed without rich flowGraph metadata only knows the
		// decimal backend id and runtime property. Reuse that canonical reference
		// instead of interpreting the decimal id as a hexadecimal MAC on re-save.
		if (
			sourceDeviceId === deviceId &&
			sourceProperty &&
			sourceProperty === String(node.data.property || '')
		) {
			return sourceRef;
		}

		let devMode = node.data.devMode as string | undefined;
		try {
			const devicesMap = get(devices) as Record<string, any>;
			const statusMap = get(devices_status) as Record<string, any>;
			const settingsMap = get(devices_settings) as Record<string, any>;
			const device = devicesMap[deviceId];
			if (device) {
				const rid = real_id(deviceId);
				const liveMode = get_mode(device, statusMap[rid], settingsMap[rid]);
				if (liveMode) devMode = liveMode;
			}
		} catch {
			// fall back to stored devMode
		}

		return buildDeviceConditionRef({
			deviceId,
			channel: String(node.data.channel ?? '0'),
			property: String(node.data.property ?? 'state'),
			operator: 'eq',
			value: 0,
			devMode,
		});
	}
	return String(node.data.sourceRef || '');
}

function getSwitchSourceRef(node: FlowNode): string {
	if (node.type === 'variableSwitch') {
		const name = String(node.data.variableName || '').trim();
		return name ? `var:${name}` : '';
	}
	return getSwitchDeviceRef(node);
}

/**
 * Build a single case condition block using the expression operator form.
 * Supports eq, ne, gt, ge, lt, le, between_inc, between_exc.
 *
 * Uses { op: [{ rr: ref }, value] } — the expression-trigger form — instead of
 * { r: ref, cmpop, cmpval }. The cmpop/cmpval form is only valid in the top-level
 * scene `if` block. Inside nested if/do/else action blocks JEMMS evaluates conditions
 * via the expression operator (returning a boolean), while cmpop/cmpval returns a
 * 'true'/'false' string which does not evaluate correctly in that context.
 *
 * Operator mapping: 'ne' → 'neq' (expression form uses neq, not ne).
 */
function buildSwitchCaseCondition(ref: string, c: SwitchCase): any {
	const op = c.operator || 'eq';
	const rawVal = c.value ?? '';
	const numVal = Number(rawVal);
	const value = !Number.isNaN(numVal) && rawVal !== '' ? numVal : rawVal;

	// 'ne' maps to 'neq' in the expression form (matches JEMMS semantics)
	const exprOp = op === 'ne' ? 'neq' : op;

	if (op === 'between_inc' || op === 'between_exc') {
		const rawVal2 = c.value2 ?? '';
		const numVal2 = Number(rawVal2);
		const value2 = !Number.isNaN(numVal2) && rawVal2 !== '' ? numVal2 : rawVal2;
		const loOp = op === 'between_inc' ? 'ge' : 'gt';
		const hiOp = op === 'between_inc' ? 'le' : 'lt';
		return {
			and: [{ [loOp]: [{ rr: ref }, value] }, { [hiOp]: [{ rr: ref }, value2] }],
		};
	}

	return { [exprOp]: [{ rr: ref }, value] };
}

/**
 * Serialize a switchNode into a nested CBL if/else chain.
 *
 * When `asRoot` is true (switch is the scene root), the chain is the `do` block
 * content — the caller provides the bare `rr` trigger as the scene `if`.
 *
 * When nested (inside a branch), this returns the entire if/else structure
 * using synchronous `r` reads.
 *
 * First match wins; unmatched falls to default.
 */
function serializeSwitchNode(
	node: FlowNode,
	em: EdgeMaps,
	nodeMap: Map<string, FlowNode>,
	triggerNodes: FlowNode[],
	visited: Set<string>,
): any {
	const cases = (node.data.cases as SwitchCase[]) ?? [];
	const ref = getSwitchSourceRef(node);

	const getBranchStatements = (handleId: string): any[] => {
		const edges = (em.adjOut.get(node.id) ?? []).filter((e) => e.sourceHandle === handleId);
		const stmts: any[] = [];
		for (const edge of edges) {
			stmts.push(...serializeFromNode(edge.target, em, nodeMap, triggerNodes, new Set(visited)));
		}
		return stmts;
	};

	// Build the nested chain from last case to first (so nesting is correct).
	const defaultStmts = getBranchStatements('default');
	let chain: any = blockValue(defaultStmts);

	for (let i = cases.length - 1; i >= 0; i--) {
		const c = cases[i];
		const doStmts = getBranchStatements(`case-${i}`);
		chain = {
			if: buildSwitchCaseCondition(ref, c),
			do: blockValue(doStmts),
			else: chain,
		};
	}

	if (cases.length === 0) return blockValue(defaultStmts);
	// Tag the outermost block so SceneToFlow can identify switch-node output
	// and reconstruct the switch node instead of recreating condition nodes.
	if (chain && typeof chain === 'object' && chain.if) {
		chain._switchNodeId = node.id;
	}
	return chain;
}

/**
 * Find a switch node that has no incoming decision-style edges
 * (i.e. it is the entry point of the flow, not inside a branch).
 */
function findRootSwitchNode(nodes: FlowNode[], em: EdgeMaps): FlowNode | null {
	const switchNodes = nodes.filter((n) => n.type === 'switchNode' || n.type === 'variableSwitch');
	for (const sw of switchNodes) {
		const incoming = em.adjIn.get(sw.id) ?? [];
		// A switch is the root if nothing feeds into its input handle
		if (incoming.length === 0) return sw;
	}
	return null;
}

function serializeHysteresisNode(
	node: FlowNode,
	em: EdgeMaps,
	nodeMap: Map<string, FlowNode>,
	triggerNodes: FlowNode[],
	visited: Set<string>,
): any {
	const ref = getSwitchDeviceRef(node);
	const lower = Number(node.data.lowerThreshold);
	const upper = Number(node.data.upperThreshold);
	const branch = (handle: 'true' | 'false') => {
		const statements: any[] = [];
		for (const edge of em.adjOut.get(node.id) ?? []) {
			if (edge.sourceHandle === handle || edge.sourceHandle?.includes(handle)) {
				statements.push(
					...serializeFromNode(edge.target, em, nodeMap, triggerNodes, new Set(visited)),
				);
			}
		}
		return blockValue(dedupeFlatBranchStatementsByFid(statements));
	};
	return {
		_hysteresisNodeId: node.id,
		if: { gt: [{ rr: ref }, upper] },
		do: branch('true'),
		else: {
			if: { lt: [{ rr: ref }, lower] },
			do: branch('false'),
			else: [],
		},
	};
}

type ActionSequenceStep = Record<string, any>;

function serializeActionSequence(node: FlowNode): any | null {
	const steps = Array.isArray(node.data.steps) ? (node.data.steps as ActionSequenceStep[]) : [];

	const serializeStepsFrom = (index: number): any[] => {
		if (index >= steps.length) return [];
		const step = steps[index];
		const fid = `${node.id}-step-${index}`;
		if (step.type === 'wait') {
			const remaining = serializeStepsFrom(index + 1);
			return [
				{
					wait: Math.max(1, Number(step.value) || 1),
					units: step.units === 'min' ? 'minutes' : 'sec',
					wid: step.id || fid,
					_fid: fid,
					do: blockValue(remaining),
				},
			];
		}
		if (step.type === 'setVariable') {
			return [
				{
					_gui_type: 'set_variable',
					_gui_purpose: 'action',
					name: String(step.name || ''),
					value: step.value ?? '',
					_fid: fid,
				},
				...serializeStepsFrom(index + 1),
			];
		}
		return [
			{
				notify: step.notificationType || 'event_log',
				msg: String(step.message || ''),
				msg_type: 'push',
				_gui_type: 'notification',
				_gui_purpose: 'action',
				_fid: fid,
			},
			...serializeStepsFrom(index + 1),
		];
	};

	const statements = serializeStepsFrom(0);
	if (!statements.length) return null;
	return { blk: statements, _actionSequenceNodeId: node.id, _fid: node.id };
}

/**
 * Normalize a list of action statements into the CBL block value:
 * - empty  → []
 * - single → statement (no blk wrapper)
 * - multi  → { blk: [...] }
 */
function blockValue(statements: any[]): any {
	if (!Array.isArray(statements) || statements.length === 0) return [];
	if (statements.length === 1) return statements[0];
	return { blk: statements };
}

/**
 * Build the if block from trigger, condition, and gate nodes.
 *
 * Key insight: Trigger→Condition pairs are COMBINED into a single scene condition.
 * - TriggerNode carries execution mode (once/any/repeat)
 * - ConditionNode carries device reference + operator + value
 * - Together they produce one JSON entry using `rr` (trigger format)
 *
 * Standalone ConditionNodes (no incoming trigger) use `r` (condition format).
 * Self-contained nodes (time, weather, etc.) use their sceneData directly.
 */
function buildIfBlock(
	triggerNodes: FlowNode[],
	conditionNodes: FlowNode[],
	orGateNodes: FlowNode[],
	andGateNodes: FlowNode[],
	em: EdgeMaps,
	nodeMap: Map<string, FlowNode>,
): ISceneObj['if'] {
	const allConditionProducers = [...triggerNodes, ...conditionNodes];

	if (andGateNodes.length > 0) {
		return buildAndGateIfBlock(andGateNodes[0], allConditionProducers, em, nodeMap);
	}

	if (orGateNodes.length > 0) {
		return buildOrGateIfBlock(orGateNodes[0], allConditionProducers, em, nodeMap);
	}

	const allConditions: any[] = [];
	const processedIds = new Set<string>();

	conditionNodes.forEach((condNode) => {
		if (processedIds.has(condNode.id)) return;

		const incomingTrigger = findIncomingTrigger(condNode.id, triggerNodes, em);

		if (incomingTrigger) {
			const combined = combineTriggerCondition(incomingTrigger, condNode);
			if (combined) allConditions.push(combined);
			processedIds.add(condNode.id);
			processedIds.add(incomingTrigger.id);
		} else {
			const sceneData = extractSceneConditionData(condNode);
			if (sceneData) allConditions.push(sceneData);
			processedIds.add(condNode.id);
		}
	});

	triggerNodes.forEach((trigNode) => {
		if (processedIds.has(trigNode.id)) return;
		const sceneData = extractSceneConditionData(trigNode);
		if (sceneData) allConditions.push(sceneData);
	});

	return { or: allConditions };
}

function findIncomingTrigger(
	condNodeId: string,
	triggerNodes: FlowNode[],
	em: EdgeMaps,
): FlowNode | null {
	const incoming = em.adjIn.get(condNodeId) ?? [];
	for (const edge of incoming) {
		const trigger = triggerNodes.find((t) => t.id === edge.source);
		if (trigger) return trigger;
	}
	return null;
}

function getConditionDeviceData(node: FlowNode): { id: string; channel: string } | null {
	const dc = node.data._device_condition as any;
	const cap = node.data.sceneCapability as any;
	if (node.data.device?.id) {
		return {
			id: node.data.device.id,
			channel: String(node.data.device.channel ?? '0'),
		};
	}
	if (dc?.device?.id) {
		return {
			id: dc.device.id,
			channel: String(dc.device.channel ?? '0'),
		};
	}
	if (cap?.deviceId) {
		return {
			id: cap.deviceId,
			channel: String(cap.channel ?? '0'),
		};
	}
	return null;
}

function getConditionPropOpVal(node: FlowNode): { property: string; operator: string; value: any } {
	const dc = node.data._device_condition as any;
	const cap = node.data.sceneCapability as any;
	const property =
		node.data.cond_name ?? dc?.cond_name ?? cap?.capabilityName ?? node.data.property?.id ?? 'state';
	const operator = node.data.operator ?? dc?.operator ?? cap?.operator ?? node.data.cmpop ?? 'eq';
	const value = node.data.value ?? dc?.value ?? cap?.value ?? node.data.cmpval ?? 0;
	return { property, operator, value };
}

/**
 * Combine a TriggerNode + ConditionNode into a single scene JSON condition.
 * The trigger provides the execution mode, the condition provides the device check.
 * Kept for backward-compat with old saved graphs that still use the Trigger→Condition
 * pair pattern (deprecated in favour of unified condition nodes).
 */
function combineTriggerCondition(triggerNode: FlowNode, conditionNode: FlowNode): any {
	if (triggerNode.data.sceneData) {
		return triggerNode.data.sceneData;
	}

	const device = getConditionDeviceData(conditionNode);
	if (!device) {
		return conditionNode.data.sceneData || null;
	}

	const { property, operator, value } = getConditionPropOpVal(conditionNode);
	const rawMode = triggerNode.data.executionMode ?? 'once';
	const executionMode: 'once' | 'any' | 'repeat' =
		rawMode === 3 || rawMode === 'repeat'
			? 'repeat'
			: rawMode === 2 || rawMode === 'any'
				? 'any'
				: 'once';
	const holdOff = triggerNode.data.hold_off ?? triggerNode.data.conditionType?.valid_for_min ?? 5;

	return createDeviceTriggerFromFlow(
		{
			deviceId: device.id,
			channel: device.channel,
			property,
			operator,
			value,
		},
		executionMode,
		holdOff,
	);
}

function buildAndGateIfBlock(
	andGate: FlowNode,
	allProducers: FlowNode[],
	em: EdgeMaps,
	nodeMap: Map<string, FlowNode>,
): ISceneObj['if'] {
	const andGroups: any[][] = [];
	const triggerNodes = allProducers.filter((n) => n.type === 'trigger');

	for (const edge of sortGateInputEdges(em.adjIn.get(andGate.id) ?? [])) {
		const sourceNode = nodeMap.get(edge.source);
		if (!sourceNode) continue;

		let armBlock: any = null;
		if (sourceNode.type === 'logicGate') {
			armBlock = buildIfBlockFromDecision(sourceNode.id, em, nodeMap, triggerNodes);
		} else {
			const json = resolveNodeToSceneJson(sourceNode, triggerNodes, em);
			if (json) armBlock = { or: [json] };
		}
		if (armBlock == null) continue;
		if (isFalseBranchSourceHandle(edge.sourceHandle)) {
			armBlock = negateConditionBlock(armBlock);
		}
		if (armBlock?.or && Array.isArray(armBlock.or)) andGroups.push(armBlock.or);
		else andGroups.push([armBlock]);
	}

	return {
		and: andGroups.map((group) => ({ or: group })),
	} as any;
}

function buildOrGateIfBlock(
	orGate: FlowNode,
	allProducers: FlowNode[],
	em: EdgeMaps,
	nodeMap: Map<string, FlowNode>,
): ISceneObj['if'] {
	const triggerNodes = allProducers.filter((n) => n.type === 'trigger');
	const orConditions = collectGateInputConditions(orGate, em, nodeMap, triggerNodes);
	return { or: orConditions };
}

function collectGateInputConditions(
	gateNode: FlowNode,
	em: EdgeMaps,
	nodeMap: Map<string, FlowNode>,
	triggerNodes: FlowNode[],
): any[] {
	const conditions: any[] = [];

	for (const edge of sortGateInputEdges(em.adjIn.get(gateNode.id) ?? [])) {
		const sourceNode = nodeMap.get(edge.source);
		if (!sourceNode) continue;

		let expr: any = null;
		if (sourceNode.type === 'logicGate') {
			expr = buildIfBlockFromDecision(sourceNode.id, em, nodeMap, triggerNodes);
		} else {
			const json = resolveNodeToSceneJson(sourceNode, triggerNodes, em);
			if (json) expr = json;
		}
		if (expr == null) continue;
		if (isFalseBranchSourceHandle(edge.sourceHandle)) {
			expr = negateConditionBlock(expr);
		}
		if (expr?.or && Array.isArray(expr.or)) conditions.push(...expr.or);
		else conditions.push(expr);
	}

	return conditions;
}

function resolveNodeToSceneJson(
	node: FlowNode,
	triggerNodes: FlowNode[],
	em: EdgeMaps,
	context?: { nested?: boolean },
): any {
	if (node.type === 'condition') {
		const incomingTrigger = findIncomingTrigger(node.id, triggerNodes, em);
		if (incomingTrigger) {
			return combineTriggerCondition(incomingTrigger, node);
		}
	}
	return extractSceneConditionData(node, context);
}

function extractSceneConditionData(node: FlowNode, context?: { nested?: boolean }): any {
	// Device condition nodes with structured data must bypass sceneData so that
	// edits made in the condition inspector are actually reflected in the output.
	// sceneData is the raw CBL from the last load — returning it verbatim would
	// silently discard any property/operator/value changes the user has made.
	const dc = node.data._device_condition as any;
	const cap = node.data.sceneCapability as any;
	const hasStructuredDeviceData = !!(node.data.device?.id || dc?.device?.id || cap?.deviceId);

	if (!hasStructuredDeviceData) {
		if (node.data.sceneData) {
			const sceneData = { ...(node.data.sceneData as any) };
			sceneData._fid = sceneData._fid || node.id;
			return sceneData;
		}

		if (node.data.originalCondition) {
			const original = { ...(node.data.originalCondition as any) };
			original._fid = original._fid || node.id;
			return original;
		}
	}

	const result = nodeToSceneJson(node, context);
	return result ? { ...result, _fid: result._fid || node.id } : null;
}

function buildActionChains(
	actionNodes: FlowNode[],
	em: EdgeMaps,
): { doActions: any[]; elseActions: any[] } {
	const doActions: any[] = [];
	const elseActions: any[] = [];

	const sorted = topologicalSort(actionNodes, em);

	for (const actionNode of sorted) {
		const branch = determineBranch(actionNode, em);
		const actionJson = extractSceneActionData(actionNode);

		if (actionJson) {
			if (branch === 'else') {
				elseActions.push(actionJson);
			} else {
				doActions.push(actionJson);
			}
		}
	}

	return { doActions, elseActions };
}

function determineBranch(node: FlowNode, em: EdgeMaps): 'do' | 'else' {
	if (node.data.branch === 'else') return 'else';

	const incoming = em.adjIn.get(node.id) ?? [];
	for (const edge of incoming) {
		if (edge.sourceHandle === 'false' || edge.sourceHandle?.includes('false')) {
			return 'else';
		}
	}

	return 'do';
}

function extractSceneActionData(node: FlowNode): any {
	let actionJson: any = null;

	const hasScriptActionConfig =
		node.type === 'scriptAction' &&
		node.data.scriptId != null &&
		Boolean(node.data.deviceRealId || node.data.deviceId);

	if (hasScriptActionConfig) {
		actionJson = nodeToSceneJson(node);
	} else if (node.data.sceneData) {
		actionJson = { ...node.data.sceneData };
		if (node.data.sceneId !== undefined) {
			actionJson.trigger = String(node.data.sceneId);
			if (actionJson.r && String(actionJson.r).startsWith('blprop:')) {
				actionJson.r = `blprop:${node.data.sceneId}:enabled`;
			}
		}
		if (node.data.groupId !== undefined)
			actionJson.grpctrl = parseInt(String(node.data.groupId), 10) || 0;
		if (node.data.action !== undefined) actionJson.turn = node.data.action;
		if (node.data.playOption === 'without_if_evaluation') actionJson.skipif = true;
		if (node.data.actionType !== undefined && node.data.actionType !== 'play') {
			const setMap: Record<string, string> = {
				enable: 'true',
				disable: 'false',
				toggle: 'toggle',
			};
			actionJson.set = setMap[node.data.actionType] ?? 'true';
		}
		if (node.data.rollerPosition !== undefined) actionJson.position = node.data.rollerPosition;
		if (node.data.rollerDirection !== undefined) actionJson.direction = node.data.rollerDirection;
	} else if (node.data.originalAction) {
		actionJson = { ...node.data.originalAction };
	} else {
		actionJson = nodeToSceneJson(node);
	}

	if (!actionJson) return null;
	actionJson._fid = actionJson._fid || node.id;

	const delay = node.data.delay;
	if (delay && typeof delay === 'object' && (delay as any).value > 0) {
		const delayVal = (delay as any).value;
		const delayUnits = (delay as any).units === 'min' ? 'minutes' : 'sec';
		return {
			wait: delayVal,
			units: delayUnits,
			wid: node.data._wid || Date.now(),
			do: { blk: [actionJson] },
		};
	}

	return actionJson;
}

// ---- Utility functions ----

/**
 * Topological sort of action nodes based on edge adjacency map.
 * O(V+E) using the pre-built adjOut map.
 */
function topologicalSort(nodes: FlowNode[], em: EdgeMaps): FlowNode[] {
	const nodeMap = new Map(nodes.map((n) => [n.id, n]));
	const sorted: FlowNode[] = [];
	const visited = new Set<string>();

	function visit(nodeId: string) {
		if (visited.has(nodeId)) return;
		visited.add(nodeId);

		const outgoing = em.adjOut.get(nodeId) ?? [];
		for (const edge of outgoing) {
			if (nodeMap.has(edge.target)) {
				visit(edge.target);
			}
		}

		sorted.unshift(nodeMap.get(nodeId)!);
	}

	const nodesWithNoIncoming = nodes.filter((node) => {
		const incoming = em.adjIn.get(node.id) ?? [];
		return !incoming.some((e) => nodeMap.has(e.source));
	});

	nodesWithNoIncoming.forEach((node) => visit(node.id));

	nodes.forEach((node) => {
		if (!visited.has(node.id)) {
			sorted.push(node);
		}
	});

	return sorted;
}

function extractDeviceMetadata(scene: ISceneObj, nodes: FlowNode[]): void {
	const conditionDevices = new Set<string>();
	const actionDevices = new Set<string>();

	for (const node of nodes) {
		const deviceId = getActionDeviceIdFromNodeData(node.data);
		if (!deviceId) continue;

		const isCondition =
			node.type === 'trigger' ||
			node.type === 'condition' ||
			(node.type || '').includes('Condition');

		if (isCondition) {
			conditionDevices.add(String(deviceId));
		} else {
			actionDevices.add(String(deviceId));
		}
	}

	if (conditionDevices.size > 0) {
		scene._meta.cdi = Array.from(conditionDevices);
	}
	if (actionDevices.size > 0) {
		scene._meta.adi = Array.from(actionDevices);
	}
}

/** Node ids referenced by `_fid` under if/do/else — semantics for these nodes live in scene JSON. */
function collectFidsFromScene(scene: ISceneObj): Set<string> {
	const ids = new Set<string>();
	function walk(v: unknown): void {
		if (v === null || v === undefined) return;
		if (typeof v !== 'object') return;
		if (Array.isArray(v)) {
			for (const x of v) walk(x);
			return;
		}
		const o = v as Record<string, unknown>;
		if (typeof o._fid === 'string' && o._fid.length > 0) ids.add(o._fid);
		for (const k of Object.keys(o)) walk(o[k]);
	}
	walk(scene.if);
	walk(scene.do);
	walk(scene.else);
	return ids;
}

const getActionDeviceIdFromNodeData = (data: FlowNode['data']): string | undefined => {
	if (!data) return undefined;

	if (data.device?.id) {
		return String(data.device.id);
	}

	if (data.deviceRealId) {
		return String(data.deviceRealId);
	}

	if (data.deviceId) {
		const raw = String(data.deviceId);
		return raw.replace(/_\d+$/, '') || raw;
	}

	if ((data._device_condition as any)?.device?.id) {
		return String((data._device_condition as any).device.id);
	}

	if (data.device_id) {
		return String(data.device_id);
	}

	if (data.sceneCapability?.deviceId) {
		return String(data.sceneCapability.deviceId);
	}

	return undefined;
};

function pickScriptActionNodeData(data: any): Record<string, any> {
	if (!data) return {};

	const picked: Record<string, any> = {};

	if (data.deviceRealId) picked.deviceRealId = data.deviceRealId;
	if (data.deviceId) picked.deviceId = data.deviceId;
	if (data.deviceName) picked.deviceName = data.deviceName;
	if (data.roomId != null) picked.roomId = data.roomId;
	if (data.roomName) picked.roomName = data.roomName;
	if (data.channel != null) picked.channel = data.channel;
	if (data.scriptId != null) picked.scriptId = data.scriptId;
	if (data.scriptName) picked.scriptName = data.scriptName;
	if (data.command) picked.command = data.command;
	if (data.delay) picked.delay = data.delay;
	if (data.isLabelManuallyOverridden) {
		picked.isLabelManuallyOverridden = true;
		if (data.displayLabel) picked.displayLabel = data.displayLabel;
	}
	if (data.label) picked.label = data.label;

	return picked;
}

function pickNonActionOrphanPersistenceData(n: FlowNode): Record<string, any> | null {
	const raw = n.data as any;
	if (!raw || typeof raw !== 'object') return null;
	try {
		if (typeof structuredClone === 'function') {
			const c = structuredClone(raw) as Record<string, any>;
			delete c.displayLabel;
			return Object.keys(c).length ? c : null;
		}
	} catch {
		/* use shallow copy below */
	}
	const c = { ...raw };
	delete c.displayLabel;
	return Object.keys(c).length ? c : null;
}

function persistFlowGraphMeta(scene: ISceneObj, nodes: FlowNode[], edges: FlowEdge[]): void {
	const sceneBackedIds = collectFidsFromScene(scene);

	const sanitizedNodes = nodes.map((n) => {
		const base: Record<string, any> = {
			id: n.id,
			type: n.type,
			position: { x: n.position.x, y: n.position.y },
		};

		if (n.type === 'switchNode' || n.type === 'variableSwitch') {
			const switchData = pickSwitchNodeData(n.data);
			const pkSwitch = (n.data as any)?.paletteKey;
			if (typeof pkSwitch === 'string' && pkSwitch.length > 0) switchData.paletteKey = pkSwitch;
			base.data = switchData;
			return base;
		}

		if (
			n.type === 'hysteresisCondition' ||
			n.type === 'actionSequence' ||
			n.type === 'presenceCondition' ||
			n.type === 'virtualComponentEvent' ||
			n.type === 'virtualComponentCondition'
		) {
			base.data = pickNonActionOrphanPersistenceData(n) || {};
			return base;
		}

		if (n.type === 'note') {
			const d = (n.data || {}) as Record<string, unknown>;
			base.data = {
				noteText: typeof d.noteText === 'string' ? d.noteText : '',
				...(typeof d.paletteKey === 'string' && d.paletteKey.length > 0
					? { paletteKey: d.paletteKey }
					: {}),
				...(d.isLabelManuallyOverridden
					? { isLabelManuallyOverridden: true, displayLabel: d.displayLabel }
					: {}),
			};
			return base;
		}

		const data: Record<string, any> = {};
		const backed = sceneBackedIds.has(n.id);

		if (!backed) {
			if (ACTION_NODE_TYPES.has(n.type || '')) {
				Object.assign(data, pickActionNodeData(n.data));
				if (n.type === 'scriptAction') {
					Object.assign(data, pickScriptActionNodeData(n.data));
				}
			} else {
				const orphan = pickNonActionOrphanPersistenceData(n);
				if (orphan) Object.assign(data, orphan);
			}
		} else if (n.type === 'scriptAction') {
			Object.assign(data, pickScriptActionNodeData(n.data));
			if (n.data?.isLabelManuallyOverridden) {
				data.isLabelManuallyOverridden = true;
				if ((n.data as any).displayLabel) data.displayLabel = (n.data as any).displayLabel;
			}
		} else if (n.data?.isLabelManuallyOverridden) {
			data.isLabelManuallyOverridden = true;
			if ((n.data as any).displayLabel) data.displayLabel = (n.data as any).displayLabel;
			if ((n.data as any).label) data.label = (n.data as any).label;
		}

		const pk = (n.data as any)?.paletteKey;
		if (typeof pk === 'string' && pk.length > 0) data.paletteKey = pk;

		if (Object.keys(data).length) base.data = data;
		return base;
	});

	const sanitizedEdges = edges.map((e) => {
		const row: Record<string, any> = {
			id: e.id,
			source: e.source,
			target: e.target,
			sourceHandle: e.sourceHandle,
			targetHandle: e.targetHandle,
		};
		const edgeType = (e as any).type;
		if (edgeType) row.type = edgeType;
		const edgeData = (e as any).data;
		if (edgeData && typeof edgeData === 'object') {
			const { pathOffset: _pathOffset, ...edgeDataRest } = edgeData as Record<string, unknown>;
			if (Object.keys(edgeDataRest).length > 0) {
				row.data = edgeDataRest;
			}
		}
		return row;
	});

	const existingHistory = Array.isArray((scene as any)?._meta?.flowExecutionHistory)
		? ((scene as any)._meta.flowExecutionHistory as any[])
		: [];
	const last5History = existingHistory.slice(-5);

	(scene as any)._meta.flowGraph = {
		/** v1: scene `if`/`do`/`else` + `_fid` are semantics; graph holds layout, orphans, switch data, edge `data`. */
		version: 1,
		nodes: sanitizedNodes,
		edges: sanitizedEdges,
		savedAt: Date.now(),
	};
	(scene as any)._meta.isFlow = true;
	(scene as any)._meta.sceneType = 'flow';
	(scene as any)._meta.flowExecutionHistory = last5History;
	delete (scene as any)._meta.flowLastTrace;
}

function pickSwitchNodeData(data: any): Record<string, any> {
	if (!data) return {};
	const picked: Record<string, any> = {};
	if (data.deviceId) picked.deviceId = data.deviceId;
	if (data.deviceName) picked.deviceName = data.deviceName;
	if (data.property) picked.property = data.property;
	if (data.channel !== undefined) picked.channel = data.channel;
	if (Array.isArray(data.cases)) picked.cases = data.cases;
	if (data.propertyDisplayValue) picked.propertyDisplayValue = data.propertyDisplayValue;
	if (data.add_info_ind !== undefined) picked.add_info_ind = data.add_info_ind;
	if (data.input_index !== undefined) picked.input_index = data.input_index;
	if (data.devMode !== undefined) picked.devMode = data.devMode;
	if (data.variableName) picked.variableName = data.variableName;
	return picked;
}

function pickActionNodeData(data: any): Record<string, any> {
	if (!data) return { label: 'Action' };

	const structuredDeviceAction =
		!!data.device?.id && Array.isArray(data.actions) && data.actions.length > 0;

	const picked: Record<string, any> = {};

	if (data.isLabelManuallyOverridden) {
		picked.isLabelManuallyOverridden = true;
		if (data.displayLabel) picked.displayLabel = data.displayLabel;
		if (data.label) picked.label = data.label;
	} else {
		// Under v2+ loaders, labels are refreshed via generateNodeLabel() from semantic fields.
		const regenerableOnLoad =
			structuredDeviceAction ||
			data.notificationType ||
			data.notificationMessage ||
			data.sceneId !== undefined ||
			data.groupId !== undefined ||
			data.variable ||
			data.timerMode ||
			data.scriptId != null ||
			(data.delay && Number((data.delay as any).value) > 0);
		if (!regenerableOnLoad) {
			picked.label = data.label || 'Action';
		}
	}

	if (data.device) picked.device = data.device;
	if (Array.isArray(data.actions) && data.actions.length > 0) picked.actions = data.actions;
	if (data.delay) picked.delay = data.delay;
	if (data.timer) picked.timer = data.timer;
	if (data.timerMode) picked.timerMode = data.timerMode;
	if (data.branch) picked.branch = data.branch;
	if (data.notificationType) picked.notificationType = data.notificationType;
	if (data.notificationMessage) picked.notificationMessage = data.notificationMessage;
	if (data.pushMsgType) picked.pushMsgType = data.pushMsgType;
	if (data.sceneId !== undefined) picked.sceneId = data.sceneId;
	if (data.groupId !== undefined) picked.groupId = data.groupId;
	if (data.action) picked.action = data.action;
	if (data.actionType) picked.actionType = data.actionType;
	if (data.playOption) picked.playOption = data.playOption;
	if (data.filterType) picked.filterType = data.filterType;
	if (data.variable) picked.variable = data.variable;
	if (data.rollerPosition !== undefined) picked.rollerPosition = data.rollerPosition;
	if (data.rollerDirection) picked.rollerDirection = data.rollerDirection;
	if (data.groupType) picked.groupType = data.groupType;
	if (data.deviceRealId) picked.deviceRealId = data.deviceRealId;
	if (data.deviceId) picked.deviceId = data.deviceId;
	if (data.deviceName) picked.deviceName = data.deviceName;
	if (data.roomId != null) picked.roomId = data.roomId;
	if (data.roomName) picked.roomName = data.roomName;
	if (data.channel != null) picked.channel = data.channel;
	if (data.scriptId != null) picked.scriptId = data.scriptId;
	if (data.scriptName) picked.scriptName = data.scriptName;
	if (data.command) picked.command = data.command;

	// Omit sceneData when it only mirrors structured device actions already in `do`/`else`.
	const shouldPersistSceneData =
		data.sceneData &&
		typeof data.sceneData === 'object' &&
		data.sceneData.r &&
		!(structuredDeviceAction && data.sceneData._fid);
	if (shouldPersistSceneData) {
		picked.sceneData = data.sceneData;
	}

	return picked;
}
